import {
	Box,
	Button,
	Center,
	Flex,
	Heading,
	Spacer,
	Spinner,
	Table,
	TableContainer,
	Tbody,
	Td,
	Text,
	Tr,
	useToast,
	Badge,
	useDisclosure,
	AlertDialog,
	AlertDialogBody,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogContent,
	AlertDialogOverlay,
} from '@chakra-ui/react';
import { useEffect, useState, useRef } from 'react';
import { getAllEncargoFiles } from '../../services/encargos';
import {
	getDocumentosByEncargo,
	eliminarDocumento,
} from '../../services/documentos';
import { errorNotification } from '../../utils/Notificaciones';
import {
	AddIcon,
	CloseIcon,
	RepeatClockIcon,
	EditIcon,
	DeleteIcon,
} from '@chakra-ui/icons';
import { S3_ROUTE } from '../../data/constants';
import UploadComponent from '../UploadComponent';
import GenerarDocumentoModal from './GenerarDocumentoModal';
import FirmarDocumentoModal from './FirmarDocumentoModal';

const ListaDocumentosEncargo = ({ encargoUuid }) => {
	const [documentosManuales, setDocumentosManuales] = useState([]);
	const [documentosGenerados, setDocumentosGenerados] = useState([]);
	const [needReload, setNeedReload] = useState(true);
	const [isLoading, setIsLoading] = useState(false);
	const [mostrarSubirDocumento, setMostrarSubirDocumento] = useState(false);
	const [documentoAFirmar, setDocumentoAFirmar] = useState(null);
	const [documentoAEliminar, setDocumentoAEliminar] = useState(null);
	const [documentoAEditar, setDocumentoAEditar] = useState(null);
	const [isDeleting, setIsDeleting] = useState(false);
	const toast = useToast();
	const cancelRef = useRef();

	const {
		isOpen: isGenerarOpen,
		onOpen: onGenerarOpen,
		onClose: onGenerarClose,
	} = useDisclosure();

	const {
		isOpen: isFirmarOpen,
		onOpen: onFirmarOpen,
		onClose: onFirmarClose,
	} = useDisclosure();

	const {
		isOpen: isDeleteOpen,
		onOpen: onDeleteOpen,
		onClose: onDeleteClose,
	} = useDisclosure();

	useEffect(() => {
		const getDocumentos = async () => {
			try {
				setNeedReload(false);
				setIsLoading(true);

				// Fetch documentos manuales (S3)
				const auxManuales = await getAllEncargoFiles(encargoUuid);
				setDocumentosManuales(auxManuales);

				// Fetch documentos generados (BD)
				try {
					const auxGenerados =
						await getDocumentosByEncargo(encargoUuid);
					setDocumentosGenerados(auxGenerados);
				} catch (err) {
					// Si falla, solo mostramos manuales
					console.error(
						'Error obteniendo documentos generados:',
						err,
					);
					setDocumentosGenerados([]);
				}

				setIsLoading(false);
			} catch (err) {
				console.error(err);
				errorNotification('Error al obtener los documentos', toast);
				setIsLoading(false);
			}
		};

		needReload && encargoUuid && getDocumentos();
	}, [encargoUuid, needReload, toast]);

	const handleFirmarClick = documento => {
		setDocumentoAFirmar(documento);
		onFirmarOpen();
	};

	const handleFirmarSuccess = () => {
		setNeedReload(true);
		onFirmarClose();
	};

	const handleGenerarSuccess = () => {
		setNeedReload(true);
		setDocumentoAEditar(null);
		onGenerarClose();
	};

	const handleEditarClick = documento => {
		setDocumentoAEditar(documento);
		onGenerarOpen();
	};

	const handleEliminarClick = documento => {
		setDocumentoAEliminar(documento);
		onDeleteOpen();
	};

	const handleEliminarConfirm = async () => {
		if (!documentoAEliminar) return;

		setIsDeleting(true);
		try {
			await eliminarDocumento(documentoAEliminar.uuid);
			toast({
				title: 'Documento eliminado',
				description: 'El documento ha sido eliminado exitosamente',
				status: 'success',
				duration: 3000,
				isClosable: true,
			});
			setNeedReload(true);
			onDeleteClose();
		} catch (error) {
			console.error('Error eliminando documento:', error);
			errorNotification(
				error?.data?.message || 'Error al eliminar el documento',
				toast,
			);
		} finally {
			setIsDeleting(false);
			setDocumentoAEliminar(null);
		}
	};

	return (
		<Box p={{ base: 3, md: 8 }} border='1px' rounded='md' shadow='lg'>
			<Heading textAlign='center' mb={4}>
				Documentos
			</Heading>

			{isLoading && (
				<Box>
					<Text textAlign='center'>Cargando...</Text>
					<Center>
						<Spinner />
					</Center>
				</Box>
			)}

			{!needReload && !isLoading && (
				<Flex
					mt={{ base: 0, md: -14 }}
					direction={{ base: 'column', md: 'row' }}
					gap={{ base: 2, md: 0 }}
				>
					<Button
						rounded='none'
						colorScheme='blue'
						onClick={() => setNeedReload(true)}
						w={{ base: '100%', md: 'auto' }}
					>
						<RepeatClockIcon />
					</Button>
					<Spacer display={{ base: 'none', md: 'block' }} />
					<Button
						rounded='none'
						colorScheme='green'
						onClick={onGenerarOpen}
						mr={{ base: 0, md: 2 }}
						w={{ base: '100%', md: 'auto' }}
					>
						<EditIcon mr={1} /> Generar
					</Button>
					<Button
						rounded='none'
						colorScheme={mostrarSubirDocumento ? 'red' : 'blue'}
						onClick={() =>
							setMostrarSubirDocumento(!mostrarSubirDocumento)
						}
						w={{ base: '100%', md: 'auto' }}
					>
						{mostrarSubirDocumento ? <CloseIcon /> : <AddIcon />}
					</Button>
				</Flex>
			)}

			<UploadComponent
				uuid={encargoUuid}
				esPedido={false}
				toast={toast}
				mostrarSubirDocumento={mostrarSubirDocumento}
				setMostrarSubirDocumento={setMostrarSubirDocumento}
			/>

			{documentosManuales.length === 0 &&
				documentosGenerados.length === 0 && (
					<Text py={6} fontWeight={'bold'} textAlign={'center'}>
						No hay documentos para este encargo
					</Text>
				)}

			{!needReload && !isLoading && (
				<TableContainer
					maxH='40rem'
					overflowY='auto'
					overflowX={{ base: 'auto', md: 'visible' }}
					w='100%'
				>
					<Table>
						<Tbody>
							{/* Documentos generados */}
							{documentosGenerados &&
								documentosGenerados.length > 0 &&
								documentosGenerados.map(documento => (
									<Tr
										key={documento.uuid}
										_hover={{
											bgColor: 'gray.100',
										}}
									>
										<Td>
											<Flex
												gap={2}
												direction={{
													base: 'column',
													md: 'row',
												}}
												align={{
													base: 'stretch',
													md: 'center',
												}}
											>
												<Text>
													{documento.tipo.replace(
														/_/g,
														' ',
													)}
												</Text>
												<Badge
													colorScheme={
														documento.estado ===
														'signed'
															? 'green'
															: documento.estado ===
																  'draft'
																? 'yellow'
																: 'red'
													}
												>
													{documento.estado ===
													'signed'
														? 'Firmado'
														: documento.estado ===
															  'draft'
															? 'Borrador'
															: 'Cancelado'}
												</Badge>
											</Flex>
										</Td>
										<Td>
											<Flex
												gap={2}
												direction={{
													base: 'column',
													md: 'row',
												}}
												align={{
													base: 'stretch',
													md: 'center',
												}}
											>
												{documento.estado ===
													'draft' && (
													<>
														<Button
															size='sm'
															colorScheme='yellow'
															onClick={() =>
																handleEditarClick(
																	documento,
																)
															}
															w={{
																base: '100%',
																md: 'auto',
															}}
														>
															<EditIcon />
														</Button>
														<Button
															size='sm'
															colorScheme='blue'
															onClick={() =>
																handleFirmarClick(
																	documento,
																)
															}
															w={{
																base: '100%',
																md: 'auto',
															}}
														>
															Firmar
														</Button>
														<Button
															size='sm'
															colorScheme='red'
															onClick={() =>
																handleEliminarClick(
																	documento,
																)
															}
															w={{
																base: '100%',
																md: 'auto',
															}}
														>
															<DeleteIcon />
														</Button>
													</>
												)}
												{documento.s3_url_draft && (
													<Button
														as='a'
														size='sm'
														colorScheme='gray'
														href={
															documento.s3_url_draft
														}
														target='_blank'
														rel='noopener noreferrer'
														w={{
															base: '100%',
															md: 'auto',
														}}
													>
														Ver Borrador
													</Button>
												)}
												{documento.s3_url_signed && (
													<Button
														as='a'
														size='sm'
														colorScheme='green'
														href={
															documento.s3_url_signed
														}
														target='_blank'
														rel='noopener noreferrer'
														w={{
															base: '100%',
															md: 'auto',
														}}
													>
														Ver Firmado
													</Button>
												)}
											</Flex>
										</Td>
									</Tr>
								))}

							{/* Documentos manuales */}
							{documentosManuales &&
								documentosManuales.length > 0 &&
								documentosManuales.map(documento => (
									<Tr
										_hover={{
											bgColor: 'gray.100',
											cursor: 'pointer',
										}}
										key={documento}
									>
										<Td
											wordBreak='break-word'
											overflowWrap='anywhere'
											maxW={{
												base: '16rem',
												md: 'unset',
											}}
										>
											<a
												href={`${S3_ROUTE}/encargos/${encargoUuid}/${documento}`}
												target='_blank'
												rel='noopener noreferrer'
											>
												{documento}
											</a>
										</Td>
										<Td></Td>
									</Tr>
								))}
						</Tbody>
					</Table>
				</TableContainer>
			)}

			<GenerarDocumentoModal
				isOpen={isGenerarOpen}
				onClose={onGenerarClose}
				encargoUuid={encargoUuid}
				onSuccess={handleGenerarSuccess}
				documentoParaEditar={documentoAEditar}
			/>

			<FirmarDocumentoModal
				isOpen={isFirmarOpen}
				onClose={onFirmarClose}
				documento={documentoAFirmar}
				onSuccess={handleFirmarSuccess}
			/>

			<AlertDialog
				isOpen={isDeleteOpen}
				leastDestructiveRef={cancelRef}
				onClose={onDeleteClose}
			>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize='lg' fontWeight='bold'>
							Eliminar Documento
						</AlertDialogHeader>

						<AlertDialogBody>
							¿Estás seguro de que deseas eliminar este documento?
							Esta acción no se puede deshacer.
						</AlertDialogBody>

						<AlertDialogFooter>
							<Button ref={cancelRef} onClick={onDeleteClose}>
								Cancelar
							</Button>
							<Button
								colorScheme='red'
								onClick={handleEliminarConfirm}
								ml={3}
								isLoading={isDeleting}
							>
								Eliminar
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</Box>
	);
};

export default ListaDocumentosEncargo;
